import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { HomeIcon, ArrowLeftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

const NotFoundPage = () => {
  return (
    <div className="min-h-screen bg-primary-900 flex items-center justify-center section-padding">
      <div className="container-width">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-8"
          >
            <Logo size="xl" variant="circular" className="mx-auto" />
          </motion.div>

          {/* 404 Error */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-8"
          >
            <h1 className="text-6xl md:text-8xl font-bold text-cream-100 mb-4 font-serif">404</h1>
            <div className="flex items-center justify-center space-x-2 text-cream-200 mb-6">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span className="text-lg">Page Not Found</span>
            </div>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-4">
              This page has moved to deeper liquidity
            </h2>
            <p className="text-body-lg text-cream-200 max-w-2xl mx-auto leading-relaxed">
              The page you're looking for might have been removed, had its name changed, 
              or is temporarily unavailable. Let's get you back to navigating our 
              strategic advisory platform.
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <Link 
              to="/" 
              className="btn-primary group"
            >
              <HomeIcon className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
              Return Home
            </Link>
            
            <button 
              onClick={() => window.history.back()}
              className="btn-secondary group"
            >
              <ArrowLeftIcon className="mr-2 h-5 w-5 transition-transform duration-300 group-hover:-translate-x-1" />
              Go Back
            </button>
          </motion.div>

          {/* Helpful Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1 }}
            className="mt-16 pt-8 border-t border-white/10"
          >
            <h3 className="text-cream-100 font-medium mb-6">Popular Sections</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Link 
                to="/about" 
                className="text-cream-200 hover:text-cream-100 transition-colors duration-300 p-3 glass-card hover:bg-white/5"
              >
                About Us
              </Link>
              <Link 
                to="/services" 
                className="text-cream-200 hover:text-cream-100 transition-colors duration-300 p-3 glass-card hover:bg-white/5"
              >
                Services
              </Link>
              <Link 
                to="/analytics" 
                className="text-cream-200 hover:text-cream-100 transition-colors duration-300 p-3 glass-card hover:bg-white/5"
              >
                Analytics
              </Link>
              <Link 
                to="/contact" 
                className="text-cream-200 hover:text-cream-100 transition-colors duration-300 p-3 glass-card hover:bg-white/5"
              >
                Contact
              </Link>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="mt-12 text-cream-300 text-sm"
          >
            <p>
              Need assistance? Contact us at{' '}
              <a 
                href="mailto:hello@valeon.finance" 
                className="text-accent-gold hover:text-accent-blue transition-colors duration-300"
              >
                hello@valeon.finance
              </a>
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFoundPage;